import React, { useState, useCallback, useRef, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { 
  FileText, 
  Building2, 
  Calculator, 
  Briefcase, 
  Users, 
  Upload, 
  Shield, 
  Lock,
  Phone, 
  Mail, 
  X, 
  CheckCircle,
  Loader2,
  Menu,
  ChevronRight,
  Newspaper
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Info
const COMPANY = {
  name: "Ali Bookkeeping",
  logo: "https://customer-assets.emergentagent.com/job_finance-assist-40/artifacts/bwfq66to_Ali%20Bookkeeping%20logo%20resized.png",
  phone: "(780) 886-3816",
  email: "hamza@alibookkeeping.com"
};

// Services Data
const SERVICES = [
  {
    icon: FileText,
    title: "Personal Tax Preparation",
    description: "Expert preparation of individual tax returns, maximizing your deductions and ensuring compliance with current tax laws."
  },
  {
    icon: Building2,
    title: "Corporate Tax Preparation",
    description: "Comprehensive corporate tax services for businesses of all sizes, from startups to established enterprises."
  },
  {
    icon: Calculator,
    title: "Compilation Engagements",
    description: "Professional compilation of financial statements that meet accounting standards and stakeholder requirements."
  },
  {
    icon: Briefcase,
    title: "Monthly Bookkeeping",
    description: "Reliable monthly bookkeeping services to keep your financial records accurate and up-to-date."
  },
  {
    icon: Users,
    title: "Payroll Services",
    description: "Complete payroll management including calculations, remittances, and year-end reporting."
  }
];

// Document Types
const DOCUMENT_TYPES = [
  { value: "tax_return", label: "Tax Return" },
  { value: "invoice", label: "Invoice" },
  { value: "receipt", label: "Receipt" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "payroll", label: "Payroll Document" },
  { value: "financial_statement", label: "Financial Statement" },
  { value: "other", label: "Other" }
];

// Navigation Component
const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-blur bg-[#050505]/90 border-b border-[#27272A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="navbar-home-link">
              <img 
                src={COMPANY.logo} 
                alt={COMPANY.name} 
                className="h-14 md:h-16 w-auto"
                data-testid="company-logo"
              />
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('services')}
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('upload')}
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="nav-upload"
            >
              Upload Documents
            </button>
            <Link 
              to="/blog"
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="nav-blog"
            >
              Blog
            </Link>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </button>
          </div>

          <Button 
            onClick={() => scrollToSection('contact')}
            className="hidden md:flex bg-[#F07D35] hover:bg-[#D66520] text-white rounded-full px-6 btn-primary"
            data-testid="nav-cta"
          >
            Get Started
          </Button>

          <button 
            className="md:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-[#27272A]">
            <div className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('services')}
                className="text-[#A1A1AA] hover:text-white transition-colors text-left py-2"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('upload')}
                className="text-[#A1A1AA] hover:text-white transition-colors text-left py-2"
              >
                Upload Documents
              </button>
              <Link 
                to="/blog"
                onClick={() => setMobileOpen(false)}
                className="text-[#A1A1AA] hover:text-white transition-colors text-left py-2"
                data-testid="nav-blog-mobile"
              >
                Blog
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-[#A1A1AA] hover:text-white transition-colors text-left py-2"
              >
                Contact
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                className="bg-[#F07D35] hover:bg-[#D66520] text-white rounded-full w-full btn-primary mt-2"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      <div className="absolute inset-0 hero-glow" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="mb-8">
          <img 
            src={COMPANY.logo} 
            alt={COMPANY.name} 
            className="h-40 md:h-56 lg:h-64 w-auto mx-auto"
            data-testid="hero-logo"
          />
        </div>
        
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          data-testid="hero-headline"
        >
          Precision Bookkeeping for Your{' '}
          <span className="text-[#F07D35]">Peace of Mind</span>
        </h1>
        
        <p 
          className="text-base md:text-lg text-[#A1A1AA] mb-10 max-w-2xl mx-auto leading-relaxed"
          data-testid="hero-subheadline"
        >
          Expert tax preparation and financial management for businesses and individuals. 
          Trust your numbers to professionals who care about your success.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={() => scrollToSection('contact')}
            className="bg-[#F07D35] hover:bg-[#D66520] text-white font-semibold rounded-full px-8 py-6 text-base btn-primary"
            data-testid="hero-cta-primary"
          >
            Get Started
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
          <Button 
            onClick={() => scrollToSection('services')}
            variant="outline"
            className="border-[#27272A] hover:border-[#F07D35] text-white rounded-full px-8 py-6 text-base bg-transparent"
            data-testid="hero-cta-secondary"
          >
            Our Services
          </Button>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="services-title">
            Our Services
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Comprehensive financial services tailored to meet your personal and business needs
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service, index) => (
            <Card 
              key={index}
              className="service-card bg-[#121212] border-[#27272A] hover:border-[#F07D35]/50"
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-8">
                <div className="icon-container w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-[#F07D35]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Document Upload Section
const UploadSection = () => {
  const [files, setFiles] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!clientName || !clientEmail || !documentType || files.length === 0) {
      toast.error("Please fill in all fields and select at least one file");
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('client_name', clientName);
      formData.append('client_email', clientEmail);
      formData.append('document_type', documentType);

      try {
        await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        errorCount++;
      }
    }

    setUploading(false);

    if (errorCount === 0) {
      toast.success(`${successCount} file(s) uploaded successfully!`);
      setFiles([]);
      setClientName('');
      setClientEmail('');
      setDocumentType('');
    } else {
      toast.error(`${errorCount} file(s) failed to upload. ${successCount} succeeded.`);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <section id="upload" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#0A0A0A]">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-[#F07D35]" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white" data-testid="upload-title">
              Secure Document Upload
            </h2>
          </div>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            Safely upload your financial documents. Your files are encrypted and securely stored.
          </p>
        </div>

        <Card className="bg-[#121212] border-[#27272A]">
          <CardContent className="p-8">
            {/* Client Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                  Your Name *
                </label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="John Doe"
                  className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35]"
                  data-testid="upload-client-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                  Your Email *
                </label>
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35]"
                  data-testid="upload-client-email"
                />
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                Document Type *
              </label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger 
                  className="bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#F07D35]"
                  data-testid="upload-document-type"
                >
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-[#27272A]">
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem 
                      key={type.value} 
                      value={type.value}
                      className="text-white hover:bg-[#27272A] focus:bg-[#27272A]"
                    >
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dropzone */}
            <div
              className={`upload-zone rounded-xl p-12 text-center cursor-pointer mb-6 ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.zip"
                data-testid="upload-file-input"
              />
              <div className="flex flex-col items-center">
                <div className="icon-container w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <Upload className="h-8 w-8 text-[#F07D35]" />
                </div>
                <p className="text-white font-medium mb-2">
                  {dragActive ? 'Drop files here' : 'Drag & drop files here or click to browse'}
                </p>
                <p className="text-[#52525B] text-sm">
                  PDF, DOC, XLS, CSV, TXT, JPG, PNG, ZIP (Max 50MB)
                </p>
              </div>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div className="mb-6">
                <h4 className="text-white font-medium mb-4">Selected Files ({files.length})</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {files.map((file, index) => (
                    <div 
                      key={index}
                      className="file-item flex items-center justify-between bg-[#0A0A0A] p-3 rounded-lg border border-[#27272A]"
                      data-testid={`upload-file-item-${index}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-[#F07D35] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{file.name}</p>
                          <p className="text-[#52525B] text-xs">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="text-[#52525B] hover:text-[#EF4444] transition-colors p-1"
                        data-testid={`upload-remove-file-${index}`}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 mb-6 text-[#52525B]">
              <Lock className="h-4 w-4" />
              <span className="text-sm">Your files are encrypted and securely stored</span>
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className="w-full bg-[#F07D35] hover:bg-[#D66520] text-white font-semibold rounded-full py-6 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-submit-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 spinner" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-5 w-5" />
                  Upload Files
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name,
        email,
        phone,
        message
      });
      
      toast.success("Message sent successfully! We'll get back to you soon.");
      setName('');
      setEmail('');
      setPhone('');
      setMessage('');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" data-testid="contact-title">
              Get in Touch
            </h2>
            <p className="text-[#A1A1AA] mb-10 leading-relaxed">
              Ready to take control of your finances? Contact us today to discuss how we can help 
              with your bookkeeping and tax preparation needs.
            </p>

            <div className="space-y-6">
              <a 
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center group-hover:border-[#F07D35] transition-colors">
                  <Phone className="h-5 w-5 text-[#F07D35]" />
                </div>
                <div>
                  <p className="text-[#52525B] text-sm">Phone</p>
                  <p className="text-white font-medium group-hover:text-[#F07D35] transition-colors">
                    {COMPANY.phone}
                  </p>
                </div>
              </a>

              <a 
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="icon-container w-12 h-12 rounded-xl flex items-center justify-center group-hover:border-[#F07D35] transition-colors">
                  <Mail className="h-5 w-5 text-[#F07D35]" />
                </div>
                <div>
                  <p className="text-[#52525B] text-sm">Email</p>
                  <p className="text-white font-medium group-hover:text-[#F07D35] transition-colors">
                    {COMPANY.email}
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Name *
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35]"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35]"
                    data-testid="contact-form-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Phone (Optional)
                  </label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(123) 456-7890"
                    className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35]"
                    data-testid="contact-form-phone"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
                    Message *
                  </label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help you?"
                    rows={4}
                    className="input-dark bg-[#0A0A0A] border-[#27272A] text-white placeholder:text-[#52525B] focus:border-[#F07D35] resize-none"
                    data-testid="contact-form-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#F07D35] hover:bg-[#D66520] text-white font-semibold rounded-full py-6 btn-primary disabled:opacity-50"
                  data-testid="contact-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src={COMPANY.logo} 
              alt={COMPANY.name} 
              className="h-14 md:h-16 w-auto"
              data-testid="footer-logo"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-[#A1A1AA] text-sm">
            <Link to="/blog" className="hover:text-[#F07D35] transition-colors" data-testid="footer-blog-link">
              Blog
            </Link>
            <a href={`tel:${COMPANY.phone}`} className="hover:text-[#F07D35] transition-colors">
              {COMPANY.phone}
            </a>
            <a href={`mailto:${COMPANY.email}`} className="hover:text-[#F07D35] transition-colors">
              {COMPANY.email}
            </a>
          </div>
          
          <p className="text-[#52525B] text-sm">
            © {currentYear} {COMPANY.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Blog Section (Soro embed)
const BlogPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const SCRIPT_SRC = "https://app.trysoro.com/api/embed/adc2eb68-ae1a-4573-9c35-2aa0664682ef";

    // Remove any previous instance so the embed re-initializes on revisit
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return (
    <section className="min-h-screen pt-28 md:pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="blog-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Newspaper className="h-8 w-8 text-[#F07D35]" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="blog-title">
              Insights & Resources
            </h1>
          </div>
          <p className="text-[#A1A1AA] max-w-xl mx-auto">
            Tax tips, bookkeeping best practices, and financial guidance to help your business thrive.
          </p>
        </div>

        <div id="soro-blog" data-testid="soro-blog-embed" />
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    const target = location.state?.scrollTo;
    if (target) {
      const element = document.getElementById(target);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [location.state]);

  return (
    <main>
      <HeroSection />
      <div className="section-divider" />
      <ServicesSection />
      <UploadSection />
      <ContactSection />
    </main>
  );
};

// Main App
function App() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121212',
            border: '1px solid #27272A',
            color: '#FFFFFF'
          }
        }}
      />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/blog" element={<BlogPage />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default App;
